import type { CSSProperties, FC, ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { useAudioTrigger } from '../../../../controllers/audioController/hooks';
import { useModifiers } from '../../../../styles/hooks/useModifiers';
import { Tooltip } from '../../../tooltip/Tooltip';
import { ChipConnected } from '../../chip/ChipConnected';
import { ChipStackButton } from '../chipStackButton';
import type { ChipStackProps } from '../types';

import styles from './revolverChipStack.module.css';

export const RevolverChipStack: FC<ChipStackProps> = ({
  areBetsOpen,
  chips,
  disabled,
  nextChip,
  onChipSelect,
  previousChip,
  selectedChip,
}): ReactElement => {
  const [open, setOpen] = useState(false);

  const audioTrigger = useAudioTrigger();
  const handleToggle = (): void => {
    if (!disabled) {
      setOpen((prev) => !prev);
      audioTrigger.play({ name: 'chipClick' });
    }
  };

  const close = (): void => {
    setOpen(false);
  };

  useEffect(() => {
    if (disabled) {
      close();
    }
  }, [disabled]);

  useEffect(() => {
    close();
  }, [areBetsOpen]);

  const positions: { verticalOffset: number; horizontalOffset: number; delay: number }[] = useMemo(() => {
    const lastChipIndex = chips.length - 1;
    return chips.map((chip, index) => {
      const chipRelativePosition = index / lastChipIndex;
      return {
        verticalOffset: Math.sin(chipRelativePosition * Math.PI),
        horizontalOffset: Math.cos(chipRelativePosition * Math.PI),
        delay: Math.sqrt(index),
      };
    });
  }, [chips]);

  const tooltipDisabled = !disabled;

  const mods = useModifiers(styles, 'revolverChipStack', { open });
  const triggerMods = useModifiers(styles, 'revolverChipStack__trigger', { disabled });
  return (
    <div data-testid="chip-stack-revolver" className={`${styles.revolverChipStack} ${mods}`}>
      <Tooltip text="INSUFFICIENTFUNDS" displayOnTouch disabled={tooltipDisabled}>
        <button className={`${styles.revolverChipStack__trigger} ${triggerMods}`} onClick={handleToggle} type="button">
          <span className={styles.revolverChipStack__innerBtnWrapper}>
            {previousChip ? (
              <span className={styles.revolverChipStack__ear} key="revolverChipStack__ear_previousChip">
                <ChipConnected size="inherit" value={previousChip.value} weight={previousChip.weight} />
              </span>
            ) : null}
            {selectedChip ? (
              <span className={styles.revolverChipStack__chipWrapper}>
                <span className={styles.revolverChipStack__chip} key="revolverChipStack__chip_selectedChip">
                  <ChipConnected size="inherit" value={selectedChip.value} weight={selectedChip.weight} />
                </span>
              </span>
            ) : null}
            {nextChip ? (
              <span className={styles.revolverChipStack__ear} key="revolverChipStack__ear_nextChip">
                <ChipConnected size="inherit" value={nextChip.value} weight={nextChip.weight} />
              </span>
            ) : null}
          </span>
        </button>
        <div className={styles.revolverChipStack__drawerSubstrate} onClick={close}></div>
        <div className={styles.revolverChipStack__drawer}>
          <div
            data-testid="revolver-chip-stack-drawer-background"
            className={styles.revolverChipStack__drawerBackground}
          ></div>
          {chips.map((chip, index) => {
            return (
              <Tooltip
                text="INSUFFICIENTFUNDS"
                disabled={!chip.disabled || chip.toolTipDisabled}
                displayOnTouch
                key={chip.weight}
                className={styles.revolverChipStack__buttonWrapper}
                style={
                  {
                    '--vertical': positions[index].verticalOffset,
                    '--horizontal': positions[index].horizontalOffset,
                    '--delay': positions[index].delay,
                  } as CSSProperties
                }
              >
                <ChipStackButton
                  active={selectedChip?.weight === chip.weight}
                  chip={chip}
                  disabled={chip.disabled}
                  onChipSelect={onChipSelect}
                  onClick={close}
                  tabIndex={open ? 0 : -1}
                />
              </Tooltip>
            );
          })}
        </div>
      </Tooltip>
    </div>
  );
};
